import { localParaMundo, localizarPontoNoMundo } from './ancoragem'
import { parametrosDoObjeto } from './parametros'
import { sentidoDaFlexao } from './flexaoEscadaGeometria'
import type { CenaTabletop, ObjetoTabletop, VetorTabletop } from './tipos'

/**
 * Quanto cada escada da cena flexiona para o lado do peso. A carga vem das
 * cordas de tração (e a guia da tirolesa) que passam por peças presas à escada (a polia do topo, o
 * mosquetão do meio): cada peça puxa a escada com a resultante das duas
 * pernas da corda, de tensão igual ao peso içado. A parte dessa força
 * perpendicular à escada, no plano dela, a curva (viga engastada no pé).
 *
 * É um modelo didático: 100 kgf perpendiculares no topo de uma escada de 6 m
 * dão ~8 cm de flecha, e o resultado é limitado a `FLEXAO_MAXIMA_M`.
 */

type V = VetorTabletop

/** Peso içado considerado, em kgf, quando há maca na cena (maca + vítima equipada). */
export const CARGA_ICADA_PADRAO_KGF = 120
/** Flecha no topo, por kgf perpendicular, numa escada de 6 m (m/kgf). */
export const FLEXAO_POR_KGF_M = 0.0008
export const COMPRIMENTO_DE_REFERENCIA_M = 6
export const FLEXAO_MAXIMA_M = 0.3
/** Abaixo disto a escada é desenhada reta. */
export const FLEXAO_MINIMA_M = 0.004

const sub = (a: V, b: V): V => [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
const comp = (a: V) => Math.hypot(a[0], a[1], a[2])
const prod = (a: V, b: V) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
const unit = (a: V): V => {
  const n = comp(a)
  return n > 1e-9 ? [a[0] / n, a[1] / n, a[2] / n] : [0, 0, 0]
}

/** Tensão da corda em relação ao peso içado: a de tração leva o peso; a guia da tirolesa, mais (ângulo raso). */
const TENSAO_RELATIVA_A_CARGA: Partial<Record<string, number>> = { tracao: 1, tirolesa: 1.5 }

/** Para onde a escada flexiona, em coordenadas do mundo (unitário). */
function sentidoNoMundo(escada: ObjetoTabletop, anguloGraus: number): V {
  const n = sentidoDaFlexao(anguloGraus)
  return unit(sub(localParaMundo(n, escada.transformacao), localParaMundo([0, 0, 0], escada.transformacao)))
}

/** A flecha, em metros, no topo da escada (negativa se ela se curva para o lado oposto ao do peso), ou 0 se não leva carga que a curve. */
export function flexaoDaEscada(cena: CenaTabletop, escada: ObjetoTabletop): number {
  if (escada.tipo !== 'escada') return 0
  const cargaKgf = (cena.objetos ?? []).some((o) => o.tipo === 'maca') ? CARGA_ICADA_PADRAO_KGF : 0
  if (cargaKgf <= 0) return 0
  const p = parametrosDoObjeto(escada)
  const angulo = p.anguloGraus!
  const comprimento = p.comprimentoM!
  const n = sentidoNoMundo(escada, angulo)
  const pe = localParaMundo([0, 0, 0], escada.transformacao)
  const eixo = unit(sub(localParaMundo([0, Math.sin((angulo * Math.PI) / 180), Math.cos((angulo * Math.PI) / 180)], escada.transformacao), pe))
  const cordas = cena.cordas ?? []
  const objetos = cena.objetos ?? []

  // as peças presas à escada por uma corda ou fita
  const presas = new Map<string, { ponto: V; s: number }>()
  for (const corda of cordas) {
    for (let i = 0; i < corda.percurso.length - 1; i += 1) {
      const a = corda.percurso[i]!
      const b = corda.percurso[i + 1]!
      const naEscada = a.idObjeto === escada.id ? a : b.idObjeto === escada.id ? b : null
      const outra = naEscada === a ? b : a
      if (!naEscada || outra.idObjeto === escada.id || corda.funcao === 'estabilizacao') continue
      const ponto = localizarPontoNoMundo(cena, naEscada)
      if (ponto) presas.set(outra.idObjeto, { ponto, s: prod(sub(ponto, pe), eixo) })
    }
  }
  // as ancoragens em V (dois anéis de fita nos banzos, unidos por um mosquetão) também prendem uma peça à escada
  for (const ancoragem of cena.ancoragens ?? []) {
    const naEscada = ancoragem.fixacoes.find((f) => f.idObjeto === escada.id)
    if (!naEscada || ancoragem.centro.idObjeto === escada.id) continue
    const ponto = localizarPontoNoMundo(cena, naEscada)
    if (ponto) presas.set(ancoragem.centro.idObjeto, { ponto, s: prod(sub(ponto, pe), eixo) })
  }

  let flecha = 0
  for (const [idPeca, { s }] of presas) {
    if (!objetos.some((o) => o.id === idPeca)) continue
    // resultante das pernas das cordas de tração que passam pela peça
    let resultante: V = [0, 0, 0]
    for (const corda of cordas) {
      const tensaoRelativa = TENSAO_RELATIVA_A_CARGA[corda.funcao]
      if (!tensaoRelativa) continue
      corda.percurso.forEach((ref, i) => {
        if (ref.idObjeto !== idPeca) return
        const aqui = localizarPontoNoMundo(cena, ref)
        if (!aqui) return
        for (const vizinho of [corda.percurso[i - 1], corda.percurso[i + 1]]) {
          if (!vizinho || vizinho.idObjeto === idPeca) continue
          const alvo = localizarPontoNoMundo(cena, vizinho)
          if (alvo) {
            const u = unit(sub(alvo, aqui))
            resultante = [resultante[0] + u[0] * tensaoRelativa, resultante[1] + u[1] * tensaoRelativa, resultante[2] + u[2] * tensaoRelativa]
          }
        }
      })
    }
    // com sinal: a escada se curva para onde a força perpendicular a empurra (para baixo, ou para o lado da guia)
    const forcaPerpendicularKgf = prod(resultante, n) * cargaKgf
    const fracao = Math.min(1, Math.max(0, s / comprimento))
    // carga a `fracao` do pé: a flecha no topo cresce com a forma da viga
    flecha += forcaPerpendicularKgf * FLEXAO_POR_KGF_M * ((fracao * fracao * (3 - fracao)) / 2)
  }
  const escalaDoComprimento = (comprimento / COMPRIMENTO_DE_REFERENCIA_M) ** 3
  const total = Math.min(FLEXAO_MAXIMA_M, Math.abs(flecha) * escalaDoComprimento) * Math.sign(flecha)
  return Math.abs(total) < FLEXAO_MINIMA_M ? 0 : total
}

/**
 * A cena com a flexão de cada escada gravada em `parametros.flexaoM`. Só para
 * exibir: os pontos de ancoragem e o desenho da escada leem esse valor, e as
 * cordas acompanham a escada curvada. A cena de verdade não é alterada.
 */
export function cenaComFlexaoDeEscadas(cena: CenaTabletop): CenaTabletop {
  if (!cena.objetos?.some((o) => o.tipo === 'escada')) return cena
  let mudou = false
  const objetos = cena.objetos.map((o) => {
    if (o.tipo !== 'escada') return o
    const flexaoM = flexaoDaEscada(cena, o)
    if (flexaoM === 0) return o
    mudou = true
    return { ...o, parametros: { ...o.parametros, flexaoM } }
  })
  return mudou ? { ...cena, objetos } : cena
}
